import type { AnyAction } from 'redux';
import type { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { resourceSagaUtils } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { investmentSymbolsActions } from 'app-components/flux/investment-symbols';
import { securitiesActions } from 'app-components/flux/securities';

import * as tickerLogosActions from './tickerLogosActions';
import * as tickerLogosSelectors from './tickerLogosSelectors';

type InvestmentSymbolResource = ReturnType<
  typeof investmentSymbolsActions.getInvestmentSymbolsSuccess
>['payload']['resources'][number];

export function* getTickerLogosSelectedActionWatcher(): SagaIterator {
  yield takeEvery(tickerLogosActions.getTickerLogosSelected.type, resourceSagaUtils.qcsRequest, {
    method: 'POST',
    path: '/logos/tickers',
    data: (action: ReturnType<typeof tickerLogosActions.getTickerLogosSelected>) => action.payload,
    successAction: tickerLogosActions.getTickerLogosSelectedSuccess,
    failureAction: tickerLogosActions.getTickerLogosSelectedFailure,
    showError: false, // not user actionable
  });
}

export function* getTickerLogosUpdatesActionWatcher(): SagaIterator {
  yield takeEvery(
    tickerLogosActions.getTickerLogosUpdates.type,
    function* getLogosUpdates(action: ReturnType<typeof tickerLogosActions.getTickerLogosUpdates>) {
      const modifiedAfter: string = yield select(tickerLogosSelectors.getLastSyncDate);
      if (assert(action.payload?.tickers?.length, 'no tickers provided')) {
        yield call(resourceSagaUtils.qcsRequest, {
          method: 'POST',
          path: '/logos/tickers',
          data: action.payload,
          ...(modifiedAfter && { params: { modifiedAfter } }),
          successAction: tickerLogosActions.getTickerLogosUpdatesSuccess,
          failureAction: tickerLogosActions.getTickerLogosUpdatesFailure,
          showError: false, // not user actionable
        });
      }
    },
  );
}

export function* getTickerLogosActionWatcher(): SagaIterator {
  yield takeEvery(
    tickerLogosActions.getTickerLogos.type,
    function* getLogos(action: ReturnType<typeof tickerLogosActions.getTickerLogos>) {
      if (action.payload?.tickers?.length) {
        const tickersMissing: string[] = yield select(tickerLogosSelectors.getTickersMissing, action.payload?.tickers);
        yield put(tickerLogosActions.getTickerLogosSelected({ tickers: tickersMissing }));
      } else {
        const tickersToUpdate: string[] = yield select(tickerLogosSelectors.getTickersToUpdate);
        const tickersToAdd: string[] = yield select(tickerLogosSelectors.getTickersToAdd);
        if (tickersToUpdate?.length) {
          if (tickersToAdd?.length) {
            yield put(tickerLogosActions.getTickerLogosSelected({ tickers: tickersToAdd }));
          } else {
            yield put(tickerLogosActions.getTickerLogosUpdates({ tickers: [...tickersToUpdate, ...tickersToAdd] }));
          }
        } else if (tickersToAdd?.length) {
          yield put(tickerLogosActions.getTickerLogosUpdates({ tickers: tickersToAdd }));
        }
      }
    },
  );
}

export function* getSymbolsSuccessActionWatcher(): SagaIterator {
  yield takeEvery(
    investmentSymbolsActions.getInvestmentSymbolsSuccess,
    function* getInvestmentSymbolsSuccess(
      action: ReturnType<typeof investmentSymbolsActions.getInvestmentSymbolsSuccess>,
    ) {
      const stockLogo: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'stockLogo');
      if (stockLogo && action.payload?.resources?.length) {
        const tickersMissing: string[] = yield select(
          tickerLogosSelectors.getTickersMissing,
          action.payload?.resources.map((resource: InvestmentSymbolResource) => resource.symbol),
        );
        if (tickersMissing?.length) {
          yield put(tickerLogosActions.getTickerLogosSelected({ tickers: tickersMissing }));
        }
      }
    },
  );
}

export function* getSecuritiesSuccessActionWatcher(): SagaIterator {
  yield takeEvery(securitiesActions.getSecuritiesSuccess, function* getSecuritiesSuccess(action: AnyAction) {
    const stockLogo: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'stockLogo');
    if (stockLogo && action.payload?.resources?.length) {
      yield put(tickerLogosActions.getTickerLogos());
    }
  });
}

export default [
  getTickerLogosActionWatcher,
  getTickerLogosUpdatesActionWatcher,
  getTickerLogosSelectedActionWatcher,
  getSymbolsSuccessActionWatcher,
  getSecuritiesSuccessActionWatcher,
];
